import { Fragment, useState, useEffect, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, QuestionMarkCircleIcon, PhotoIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { supportApi } from '../services/api';

interface SupportModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail: string;
  userId?: string;
}

type Category = 'bug' | 'feature' | 'billing' | 'other';

interface ScreenshotFile {
  file: File;
  preview: string;
}

const CATEGORIES: { value: Category; label: string }[] = [
  { value: 'bug', label: 'Bug Report' },
  { value: 'feature', label: 'Feature Request' },
  { value: 'billing', label: 'Billing Question' },
  { value: 'other', label: 'Other' },
];

const MAX_SCREENSHOTS = 3;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];
const MAX_SUBJECT_LENGTH = 100;
const MAX_MESSAGE_LENGTH = 2000;

export default function SupportModal({ isOpen, onClose, userEmail, userId }: SupportModalProps) {
  const [replyEmail, setReplyEmail] = useState(userEmail);
  const [category, setCategory] = useState<Category>('bug');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [screenshots, setScreenshots] = useState<ScreenshotFile[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update reply email when userEmail prop changes
  useEffect(() => {
    if (userEmail && !replyEmail) {
      setReplyEmail(userEmail);
    }
  }, [userEmail]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setReplyEmail(userEmail || '');
      setCategory('bug');
      setSubject('');
      setMessage('');
      setScreenshots([]);
    }
  }, [isOpen, userEmail]);

  // Cleanup preview URLs when component unmounts or screenshots change
  useEffect(() => {
    return () => {
      screenshots.forEach(s => URL.revokeObjectURL(s.preview));
    };
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const remainingSlots = MAX_SCREENSHOTS - screenshots.length;
    if (remainingSlots <= 0) {
      toast.error(`Maximum ${MAX_SCREENSHOTS} screenshots allowed`);
      return;
    }

    const newScreenshots: ScreenshotFile[] = [];

    for (let i = 0; i < Math.min(files.length, remainingSlots); i++) {
      const file = files[i];

      // Validate file type
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error(`${file.name}: Invalid file type. Please use PNG, JPG, WebP, or GIF.`);
        continue;
      }

      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name}: File too large. Maximum size is 5MB.`);
        continue;
      }

      newScreenshots.push({
        file,
        preview: URL.createObjectURL(file),
      });
    }

    if (newScreenshots.length > 0) {
      setScreenshots(prev => [...prev, ...newScreenshots]);
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeScreenshot = (index: number) => {
    setScreenshots(prev => {
      const removed = prev[index];
      URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isFormValid = () => {
    return (
      isValidEmail(replyEmail) &&
      subject.trim().length > 0 &&
      subject.length <= MAX_SUBJECT_LENGTH &&
      message.trim().length > 0 &&
      message.length <= MAX_MESSAGE_LENGTH
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      // Step 1: Upload screenshots if any
      const uploadedKeys: string[] = [];

      for (const screenshot of screenshots) {
        try {
          // Get presigned URL
          const uploadUrlResponse = await supportApi.getUploadUrl(
            screenshot.file.name,
            screenshot.file.type,
            screenshot.file.size
          );

          if (!uploadUrlResponse.success || !uploadUrlResponse.data) {
            console.error('Failed to get upload URL:', uploadUrlResponse);
            continue;
          }

          const { uploadUrl, fields, s3Key } = uploadUrlResponse.data;

          // Upload to S3
          await supportApi.uploadToS3(uploadUrl, fields, screenshot.file);
          uploadedKeys.push(s3Key);
        } catch (uploadError) {
          console.error('Error uploading screenshot:', uploadError);
          // Continue with other uploads
        }
      }

      // Step 2: Submit support request
      const response = await supportApi.submitRequest({
        replyEmail,
        category,
        subject,
        message,
        attachments: uploadedKeys,
        ...(userId && { userId })
      });

      if (response.success) {
        toast.success(`Support request submitted! Ticket: ${response.data?.ticketId}`);
        onClose();
      } else {
        throw new Error(response.error || 'Failed to submit request');
      }
    } catch (error: any) {
      console.error('Error submitting support request:', error);

      // Handle specific error cases
      if (error.response?.status === 429) {
        toast.error('Too many requests. Please wait before submitting again.');
      } else if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Failed to submit support request. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-semibold leading-6 text-gray-900 dark:text-white flex items-center gap-2"
                  >
                    <QuestionMarkCircleIcon className="h-6 w-6 text-primary-600" />
                    Contact Support
                  </Dialog.Title>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
                    onClick={onClose}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Reply Email */}
                  <div>
                    <label htmlFor="reply-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Reply Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="reply-email"
                      value={replyEmail}
                      onChange={(e) => setReplyEmail(e.target.value)}
                      placeholder="your@email.com"
                      required
                      className="block w-full rounded-lg border border-gray-300 dark:border-slate-600 px-4 py-2.5 text-gray-900 dark:text-white bg-white dark:bg-slate-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-sm"
                    />
                    {userEmail && replyEmail !== userEmail && replyEmail && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Different from your account email ({userEmail})
                      </p>
                    )}
                  </div>

                  {/* Category */}
                  <div>
                    <label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="category"
                      value={category}
                      onChange={(e) => setCategory(e.target.value as Category)}
                      className="block w-full rounded-lg border border-gray-300 dark:border-slate-600 px-4 py-2.5 text-gray-900 dark:text-white bg-white dark:bg-slate-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-sm"
                    >
                      {CATEGORIES.map((cat) => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Subject */}
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Subject <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="subject"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value.slice(0, MAX_SUBJECT_LENGTH))}
                      placeholder="Brief description of your issue"
                      required
                      className="block w-full rounded-lg border border-gray-300 dark:border-slate-600 px-4 py-2.5 text-gray-900 dark:text-white bg-white dark:bg-slate-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 text-right">
                      {subject.length}/{MAX_SUBJECT_LENGTH}
                    </p>
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      value={message}
                      onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE_LENGTH))}
                      placeholder="Please describe your issue or question in detail..."
                      required
                      rows={4}
                      className="block w-full rounded-lg border border-gray-300 dark:border-slate-600 px-4 py-2.5 text-gray-900 dark:text-white bg-white dark:bg-slate-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-sm resize-none"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 text-right">
                      {message.length}/{MAX_MESSAGE_LENGTH}
                    </p>
                  </div>

                  {/* Screenshots */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Screenshots <span className="text-gray-400 font-normal">(optional, max {MAX_SCREENSHOTS})</span>
                    </label>

                    {/* Screenshot Previews */}
                    {screenshots.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {screenshots.map((screenshot, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={screenshot.preview}
                              alt={`Screenshot ${index + 1}`}
                              className="w-20 h-20 object-cover rounded-lg border border-gray-300 dark:border-slate-600"
                            />
                            <button
                              type="button"
                              onClick={() => removeScreenshot(index)}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <XCircleIcon className="h-5 w-5" />
                            </button>
                            <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs px-1 py-0.5 rounded-b-lg truncate">
                              {screenshot.file.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Upload Button */}
                    {screenshots.length < MAX_SCREENSHOTS && (
                      <div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept={ALLOWED_TYPES.join(',')}
                          multiple
                          onChange={handleFileSelect}
                          className="hidden"
                          id="screenshot-upload"
                        />
                        <label
                          htmlFor="screenshot-upload"
                          className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer transition-colors"
                        >
                          <PhotoIcon className="h-5 w-5 mr-2" />
                          Add Screenshot
                        </label>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          PNG, JPG, WebP, or GIF up to 5MB each
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      type="button"
                      className="px-4 py-2.5 rounded-lg border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
                      onClick={onClose}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={!isFormValid() || isSubmitting}
                      className="px-4 py-2.5 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium flex items-center"
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Submitting...
                        </>
                      ) : (
                        'Submit Request'
                      )}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
